import { createFileRoute } from "@tanstack/react-router";
import { BugIcon } from "lucide-react";
import { SentryStatusPanel } from "@/components/admin/sentry/sentry-status-panel";
import { SentryTestConsole } from "@/components/admin/sentry/sentry-test-console";
import { requireAdminSection } from "@/lib/admin-guard";

export const Route = createFileRoute("/_admin/sentry")({
  beforeLoad: ({ context }) => requireAdminSection(context.auth.capabilities),
  component: AdminSentryPage,
});

function AdminSentryPage() {
  return (
    <div className="flex flex-1 flex-col gap-8 p-6">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
          <BugIcon className="size-3.5" />
          Diagnostics
        </div>
        <div className="flex flex-col gap-1">
          <h1 className="font-heading font-semibold text-3xl tracking-tight">Sentry</h1>
          <p className="max-w-2xl text-muted-foreground text-sm">
            Check that error reporting actually works, end to end. Each scenario below exercises a
            different capture path — browser, server render, API Worker, background jobs — because
            they're wired independently and fail independently. Trigger one, then confirm it appears
            in Sentry.
          </p>
        </div>
      </header>

      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="font-heading font-semibold text-xl tracking-tight">Configuration</h2>
          <p className="max-w-2xl text-muted-foreground text-sm">
            If events never arrive, start here: a missing DSN means nothing will ever be sent, which
            is a different problem from events being sent and not showing up.
          </p>
        </div>
        <SentryStatusPanel />
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="font-heading font-semibold text-xl tracking-tight">Test scenarios</h2>
          <p className="max-w-2xl text-muted-foreground text-sm">
            Everything triggered here is tagged{" "}
            <code className="font-mono text-xs">sentry_test:true</code> in Sentry, so you can filter
            these out of real issues — or set up a saved search to watch only for them.
          </p>
        </div>
        <SentryTestConsole />
      </section>
    </div>
  );
}
